/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { WinValue } from '@1x2network/is_engine_communication'
import { BasicLayout, OrientationMap, Orientor } from '@1x2network/layout'
import {
    applySpecification,
    ContainerSpecification,
    createSprite,
    Specification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import { SplashComponent } from '@1x2network/pixi_splash'
import { HoldAndWinReelSet, PositionTuple } from '@1x2network/reel_set'
import { HoldAndWinReelSetSpecProvidedPool } from '@1x2network/reel_set/lib/prefabs/holdandwin/HoldAndWinReelSet'
import gsap from 'gsap'
import { produce } from 'immer'
import { Container, FederatedPointerEvent, IDestroyOptions } from 'pixi.js'
import { Signal } from 'typed-signals'
import { CustomSymbol, CustomSymbolSpecification } from '../../../CustomSymbol'
import { SymbolModel } from '../../../model/SymbolModel'
import { Collection, CollectionSpecification } from '../../Collection'
import { CustomSymbolPool } from '../../CustomSymbolPool'
import { ReelFrame } from '../../ReelFrame'
import { MiddleReel, MiddleReelSpecification } from '../../MiddleReel'

/**
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class HoldAndWinCollectorLoop extends SplashComponent<HoldAndWinLoopSpecification> {
    private reelSet: HoldAndWinReelSet<CustomSymbol, SymbolModel>
    private timeline?: GSAPTimeline
    private middleReel: MiddleReel
    private orientor: Orientor<ContainerSpecification>

    constructor(options: { parent?: Container; layout: BasicLayout; spec: HoldAndWinLoopSpecification }) {
        super(options)

        const pool = new CustomSymbolPool(Specification.get('symbols') as CustomSymbolSpecification[], CustomSymbol)
        const spec = Specification.get('hawReelSet') as HoldAndWinReelSetSpecProvidedPool<SymbolModel>
        spec.spinAnim.stagger = { start: 0, stop: 0 }
        this.reelSet = new HoldAndWinReelSet<CustomSymbol, SymbolModel>({ spec, parent: this, pool })
        createSprite({
            spec: Specification.get('hawReelBackground') as SpriteSpecification,
            parent: this.reelSet,
        })
        new ReelFrame('hawReelFrame', this.reelSet)

        this.middleReel = new MiddleReel({
            spec: Specification.get('middleReel') as MiddleReelSpecification,
            backgroundParent: this.reelSet,
            foregroundParent: this.reelSet,
        })
        this.middleReel.foregroundSpine.zIndex = 2

        this.prepareSpin()

        this.orientor = new Orientor({
            layout: options.layout,
            property: this.specification,
            onOrient: (active) => {
                applySpecification(this, active)
            },
        })
    }

    private prepareSpin(): void {
        this.reelSet.prepareSpin(stopSpinResult)
    }

    public show(): void {
        super.show()

        this.animate()
    }

    public hide(): void {
        super.hide()

        this.timeline?.kill()

        this.reelSet.cancelSpin()
        this.prepareSpin()
    }

    private animate() {
        this.middleReel.show()
        const positions: PositionTuple[] = [
            [0, 0],
            [1, 2],
            [4, 1],
        ]

        const fakeClickHandler = { onClick: new Signal<(event: FederatedPointerEvent) => void>() }
        const spec = produce(Specification.get('collection') as CollectionSpecification, (draft) => {
            draft.timings.segment.stagger = 0
            draft.timings.segment.trailSpawn = 100
            draft.timings.wait.before = 0
            draft.timings.wait.startSpawn = 0
        })

        const collection = new Collection({ spec, parent: this, clickHandler: fakeClickHandler })
        const collectorSymbol = this.reelSet.fetcher.getSymbolByPosition([2, 1])

        Object.assign(collectorSymbol, {
            playCollect: () => false,
        })

        this.timeline = gsap
            .timeline({ repeat: -1, repeatDelay: 1.5 })

            // Reset the collector symbol
            .to(collectorSymbol.text!, {
                alpha: 0,
                duration: 0.3,
                onComplete: () => {
                    collectorSymbol.updateTextValue(0)
                    collectorSymbol.text!.alpha = 1
                },
            })

            // Spin
            .call(
                () => {
                    this.reelSet.lockCells([[2, 1]])
                    this.reelSet.startSpin()
                },
                [],
                '<',
            )

            // Stop spinning
            .call(
                () => {
                    this.reelSet.stopSpin({ symbols: stopSpinResult })
                },
                [],
                '+=0.7',
            )

            // Animate HAW symbols, fade out non-winning symbols
            .call(
                () => {
                    collection.animate(this.reelSet.fetcher, {
                        order: 0,
                        type: 'CASH_COLLECTION',
                        to: [2, 1] as PositionTuple,
                        items: positions.map((pos) => ({
                            from: pos,
                            cash: { currency: 1, unit: 100 },
                            cumulative: { currency: 56, unit: 5600 },
                        })),
                    })
                },
                [],
                '+=0.5',
            )
    }

    public destroy(options?: IDestroyOptions | boolean): void {
        this.orientor.disconnect()
        super.destroy(options)
    }
}

const stopSpinResult: SymbolModel[][] = [
    [
        {
            id: 12,
            cash: { start: { timesStake: 50, currency: 50 } as WinValue, end: { timesStake: 1 } as WinValue },
        },
        { id: 15 },
        { id: 15 },
    ],
    [
        { id: 15 },
        { id: 15 },
        {
            id: 10,
            cash: { start: { timesStake: 5, currency: 5 } as WinValue, end: { timesStake: 1 } as WinValue },
        },
    ],
    [
        { id: -1 },
        {
            id: 9,
            cash: { start: { timesStake: 0 } as WinValue, end: { timesStake: 0 } as WinValue },
        },
        { id: -1 },
    ],
    [{ id: 15 }, { id: 15 }, { id: 15 }],
    [
        { id: 15 },
        {
            id: 10,
            cash: {
                start: { timesStake: 1, currency: 1 } as WinValue,
                end: { timesStake: 1, currency: 1 } as WinValue,
            },
        },
        { id: 15 },
    ],
]

type ContainerSpecWithOrientation = ContainerSpecification & OrientationMap<ContainerSpecification>

export type HoldAndWinLoopSpecification = ContainerSpecWithOrientation
